import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import Cookies from "js-cookie";
import { getPlatformLogo } from "../services/api";
import { getCurrencySymbol, setActiveCurrency } from "../utils/currency";
// Define your user type (customize as per your app)
export interface User {
  id: string;
  name: string;
  email: string;
  role?: string;
  phonenumber: string;
  restaurantId: string;
  bonusWallet: {
    balance: number;
    currency: string;
  };
  wallet: {
    balance: number;
    currency: string;
  };

  // add more fields if needed
}

// Define the context type
interface AuthContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  isAuth: boolean;
  login: (userData: User) => void;
  logout: () => void;
  taxes: [TaxesProps] | null;
  setTaxes: React.Dispatch<React.SetStateAction<[TaxesProps]>>;
  categoryCodes: [categoryCodes] | null;
  setCategoryCodes: React.Dispatch<React.SetStateAction<[categoryCodes]>>;
  squareApplicationId: string | null;
  squareLocationId: string | null;
  currency: string;
  currencySymbol: string;
  setCurrency: React.Dispatch<React.SetStateAction<string>>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider props
interface AuthProviderProps {
  children: ReactNode;
}
export interface TaxesProps {
  _id: string;
  country: string;
  state: string;
  city: string;
  // US
  tax_percent?: string;
  // Canada
  gst?: string;
  pst?: string;
  category_code: string;
}
export interface categoryCodes {
  _id: string;
  code: string;
  name: string;
}
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [taxes, setTaxes] = useState<[TaxesProps]>([
    {
      _id: "",
      country: "",
      state: "",
      city: "",
      tax_percent: "",
      category_code: "",
    },
  ]);
  const [categoryCodes, setCategoryCodes] = useState<[categoryCodes]>([
    {
      _id: "",
      code: "",
      name: "",
    },
  ]);
  const [squareApplicationId, setSquareApplicationId] = useState<string | null>(
    null,
  );
  const [squareLocationId, setSquareLocationId] = useState<string | null>(null);
  const [currency, setCurrency] = useState<string>("USD");

  // Derived flag
  const isAuth = !!user;

  // Load from Cookies on mount
  useEffect(() => {
    const storedUser = Cookies.get("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  useEffect(() => {
    if (user) {
    } else {
      Cookies.remove("user");
    }
  }, [user]);

  // Fetch Square credentials on mount
  useEffect(() => {
    const fetchSquareCredentials = async () => {
      try {
        const response = await getPlatformLogo();
        if (response.data.success && response.data.square) {
          setSquareApplicationId(
            response.data.square.SQUARE_APPLICATION_ID || null,
          );
          setSquareLocationId(response.data.square.SQUARE_LOCATION_ID || null);
        }
        if (response.data.currency) {
          setCurrency(response.data.currency);
          // Also for receipts, printers and reports outside React state
          setActiveCurrency(response.data.currency);
        }
      } catch (error) {
        console.error("Failed to fetch Square credentials:", error);
      }
    };
    fetchSquareCredentials();
  }, []);

  // Auth actions
  const login = (userData: User) => setUser(userData);
  const logout = () => {
    setUser(null);
    Cookies.remove("Logintoken");
  };

  const currencySymbol = getCurrencySymbol(currency);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        isAuth,
        login,
        logout,
        taxes,
        setTaxes,
        categoryCodes,
        setCategoryCodes,
        squareApplicationId,
        squareLocationId,
        currency,
        currencySymbol,
        setCurrency,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
